import PoppinsRegular from "../../../assets/fonts/Poppins-Regular.ttf";
import PoppinsBold from "../../../assets/fonts/Poppins-Bold.ttf";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
  pdf,
} from "@react-pdf/renderer";
import logo from "../../../assets/logo.png";
import img from "../../../assets/Picture1.png";
import term from "../../../assets/termss.png";
import { FormatDate } from "../../../lib/dateFormat";
import { AmountFormat } from "../../../lib/helper/amountFormat";
import footerOne from "../../../assets/footerOne.png";
import { pdfAmountFormat } from "../../../lib/helper/pdfAmountFormat";

const getCurrencySymbol = (currency) => {
  switch (currency?.toUpperCase()) {
    case 'USD':
      return '$';
    case 'EUR':
      return '\u20ac';
    case 'GBP':
      return '\u00a3';
    default:
      return '$'; // Default fallback
  }
};

const DEFAULT_BANK_ACCOUNT = {
  accountName: "One Union Solutions B.V.",
  iban: "NL28 INGB 0006 9674 14",
  swift: "INGBNL2A",
};

const getInvoiceBankAccount = (data) => ({
  ...DEFAULT_BANK_ACCOUNT,
  ...(data?.selectedBankAccount || {}),
});

const getBillToLines = (billToText) =>
  String(billToText || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const getServiceRecipientText = (data) =>
  data?.serviceRecipientText || data?.billToText || "";

const getExternalBillToText = (data) =>
  data?.externalBillToText || "";

const isExternalInvoice = (data) =>
  Boolean(data?.isExternalInvoice || data?.invoiceSource === "external");

const wrapPdfText = (value, chunkSize = 18) =>
  String(value || "")
    .split(" ")
    .map((word) => {
      if (word.length <= chunkSize) return word;
      return word.match(new RegExp(`.{1,${chunkSize}}`, "g")).join("\n");
    })
    .join(" ");

/** Credit invoice: non-zero values render as negative; zero stays 0 */
const asCreditNegative = (value) => {
  const numeric = Number(value);
  if (!numeric || Number.isNaN(numeric)) return 0;
  return -Math.abs(numeric);
};

/** Always show a leading minus for credit figures (avoids format edge-cases) */
const formatCreditAmount = (value, currency) => {
  const numeric = Number(value);
  if (!numeric || Number.isNaN(numeric)) return "0,00";
  return `-${pdfAmountFormat(Math.abs(numeric), currency)}`;
};

const BillToBlock = ({ billToText }) => {
  const billToLines = getBillToLines(billToText);

  if (billToLines.length === 0) {
    return null;
  }

  return (
    <View style={{ marginTop: 6 }}>
      <Text style={{ fontWeight: "bold", marginBottom: 1 }}>Service Recipient:</Text>
      {billToLines.map((line, index) => (
        <Text
          key={index}
          style={{
            fontWeight: index === 0 ? "600" : "normal",
          }}
        >
          {line}
        </Text>
      ))}
    </View>
  );
};

const TextLinesBlock = ({ lines }) => {
  if (!lines.length) return null;

  return (
    <>
      {lines.map((line, index) => (
        <Text
          key={index}
          style={{
            fontWeight: index === 0 ? "600" : "normal",
          }}
        >
          {line}
        </Text>
      ))}
    </>
  );
};

Font.register({
  family: "Poppins",
  fonts: [
    {
      src: PoppinsRegular,
    },
    {
      src: PoppinsBold,
      fontWeight: "bold",
    },
  ],
});

Font.registerHyphenationCallback((word) => [word]);

const styles = StyleSheet.create({
  page: {
    fontFamily: "Poppins",
    fontSize: 8,
    paddingTop: 12,
    paddingLeft: 25,
    paddingBottom: 50, // Added to prevent overlap with footer
    lineHeight: 1.5,
    flexDirection: "column",
    justifyContent: "flex-start",
  },
  compactPage: {
    paddingTop: 10,
    paddingBottom: 36,
    lineHeight: 1.45,
  },
  page2: {
    flexDirection: "column",
    position: "relative",
  },
  footerOne: {
    position: "absolute",
    left: 0,
    bottom: 0,
    right: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: '#ffffff',
    borderBottom: "7px solid #DA1C00",
  },
  footerTextContainer: {
    maxWidth: "60%", // Match generatePDF.jsx
    textAlign: "center",
  },
  footerTextInside: {
    fontSize: 8,
    color: "gray",
    fontFamily: "Helvetica",
    textAlign: "center",
    marginTop: 5,
    marginBottom: 2, // Reduced spacing between lines
    lineHeight: 1.1, // Control line height more tightly
  },
  footerTextUrl: {
    fontSize: 8,
    color: "gray",
    fontFamily: "Helvetica",
    textAlign: "center",
    marginBottom: 5, // Space below URL to avoid overlap with red line
    lineHeight: 1.1, // Control line height more tightly
  },
  termPage: {
    height: "100vh",
    width: "100%",
  },
  terms: {
    width: "100%",
    marginBottom: 5,
  },
  logo1: {
    width: "215px",
    marginBottom: 10,
  },
  logo2: {
    width: 400,
    marginBottom: 5,
  },
  rightImage: {
    position: "absolute",
    right: 0,
    top: 0,
  },
  summaryContainer: {
    alignSelf: "flex-end",
    width: "42%",
    marginTop: 0,
  },
  summaryContent: {
    marginBottom: 3,
  },
  summaryRow: {
    flexDirection: "row",
    marginBottom: 3,
  },
  summaryLabel: {
    width: 115,
  },
  summaryValue: {
    width: 22,
    textAlign: "center",
  },
  summaryTotal: {
    width: 100,
    textAlign: "right",
  },
  divider: {
    height: 1,
    backgroundColor: "black",
    marginVertical: 5,
  },
  logo: {
    width: 162,
    marginBottom: 20,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    marginBottom: 20,
  },
  compactHeader: {
    marginBottom: 14,
  },
  flexRowBetween: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  companyInfo: {
    fontSize: 9,
  },
  invoiceTitle: {
    fontSize: 20,
    marginBottom: 10,
  },
  clientInfo: {
    marginBottom: 10,
    marginLeft: 15,
    marginTop: 4,
    rowGap: 2,
  },
  compactClientInfo: {
    marginBottom: 8,
    rowGap: 2,
  },
  titleBold: {
    fontFamily: "Poppins",
    fontWeight: "bold",
  },
  paymentInfoContainer: {
    display: "flex",
    flexDirection: "row",
    marginBottom: 10,
  },
  compactPaymentInfoContainer: {
    marginBottom: 8,
  },
  paymentInfo: {
    padding: 10,
    backgroundColor: "#f3f4f6",
    width: "80%",
    border: "1px solid #f3f4f6",
    borderColor: "#d3d3d3",
  },
  compactPaymentInfo: {
    padding: 9,
  },
  clientInfoContainer: {
    // width: "48%",
  },
  invoiceInfo: {
    width: "48%",
    display: "flex",
    alignItems: "flex-end",
  },
  paymentInfoTitle: {
    padding: 2,
    position: "absolute",
    top: -8,
    left: 5,
    borderWidth: 1,
    borderColor: "#d3d3d3",
    backgroundColor: "white",
    fontSize: 8,
    fontWeight: "bold",
    fontFamily: "Poppins",
    paddingLeft: 5,
    paddingRight: 5,
  },
  table: {
    width: "95%",
    marginBottom: 8,
    marginTop: 6,
  },
  compactTable: {
    marginBottom: 12,
    marginTop: 5,
  },
  tableHeader: {
    backgroundColor: "#f3f4f6",
    flexDirection: "row",
    fontFamily: "Poppins",
    fontWeight: "bold",
    fontSize: 8.5,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableCell: {
    padding: 1,
    paddingLeft: 10,
  },
  compactTableCell: {
    padding: 0.6,
    paddingLeft: 10,
    fontSize: 7.9,
    lineHeight: 1.35,
  },
  tableCellHeader: {
    fontWeight: "bold",
  },
  summary: {
    position: "absolute",
    left: 25,
    right: 25,
    bottom: 68,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: "#f3f4f6",
    paddingTop: 9,
    paddingBottom: 9,
    paddingLeft: 8,
    paddingRight: 8,
  },
  compactSummary: {
    bottom: 54,
  },
  bottomNote: {
    position: "absolute",
    left: 25,
    right: 25,
    bottom: 42,
    fontSize: 8,
    color: "#333333",
    textAlign: "left",
    lineHeight: 1.2,
  },
  summaryLeft: {
    width: "50%",
  },
  summaryRight: {
    textAlign: "ugi",
  },
  total: {
    fontWeight: "bold",
  },
  titleBox: {
    display: "flex",
    flexDirection: "row",
    gap: 0,
  },
});

const PDFDocument = ({ data }) => {
  const isCreditInvoice = Boolean(data?.isCreditInvoice || data?.documentType === "credit");
  const serviceRecipientText = getServiceRecipientText(data);
  const externalBillToLines = getBillToLines(getExternalBillToText(data));
  const hasBillTo = getBillToLines(serviceRecipientText).length > 0;
  const hasBottomNote = !!data?.bottomNote?.trim();
  const compactTableCellStyle = hasBillTo ? styles.compactTableCell : {};

  const services = data?.shipment?.services || [];
  // Absolute line totals — used to force credit signs (Base / totals / qty)
  const absoluteSubTotal = services.reduce((sum, item) => {
    return sum + Math.abs(Number(item?.numberOfItem || 0)) * Math.abs(Number(item?.exclVAT || 0));
  }, 0);
  const vatPercentage = Number(data?.shipment?.vatPercentage || data?.vatPercentage || 0);
  const absoluteVatAmount = Math.abs(
    Number(
      data?.shipment?.vatAmount != null
        ? data.shipment.vatAmount
        : ((absoluteSubTotal * vatPercentage) / 100).toFixed(2)
    )
  );
  const absoluteInsurance = Math.abs(Number(data?.shipment?.insurance || data?.insuranceAmount || 0));
  const showInsurance =
    absoluteInsurance > 0 ||
    data?.shipment?.isInsuranceChecked ||
    data?.isInsuranceChecked;

  let rawDiscount = null;
  if (data && Object.prototype.hasOwnProperty.call(data, 'discount')) {
    rawDiscount = data.discount;
  } else if (data?.discount !== undefined) {
    rawDiscount = data.discount;
  } else if (data?.shipment && Object.prototype.hasOwnProperty.call(data.shipment, 'discount')) {
    rawDiscount = data.shipment.discount;
  } else if (data?.shipment?.discount !== undefined) {
    rawDiscount = data.shipment.discount;
  } else {
    rawDiscount = data?.shipment?.tradeOSData?.discount || null;
  }
  let parsedDiscount = null;

  if (typeof rawDiscount === "number" || (typeof rawDiscount === "string" && !isNaN(Number(rawDiscount)))) {
    const num = Number(rawDiscount);
    if (num > 0) {
      parsedDiscount = { type: "fixed", value: num, amount: num };
    }
  } else if (rawDiscount && typeof rawDiscount === "object") {
    const amt = Number(rawDiscount.amount ?? (rawDiscount.type === "fixed" ? rawDiscount.value : 0));
    if (amt > 0) {
      parsedDiscount = {
        ...rawDiscount,
        amount: amt,
        type: rawDiscount.type || "fixed",
        value: rawDiscount.value ?? amt,
      };
    } else if (rawDiscount.type === "percentage" && Number(rawDiscount.value) > 0 && absoluteSubTotal > 0) {
      const computedAmt = (absoluteSubTotal * Number(rawDiscount.value)) / 100;
      parsedDiscount = {
        ...rawDiscount,
        amount: computedAmt,
        type: "percentage",
        value: Number(rawDiscount.value),
      };
    }
  }

  // Fallback: If no discount object is provided, but the invoice total is less than subtotal + VAT + insurance
  if (!parsedDiscount && !isCreditInvoice) {
    const effectiveSubTotal = Number(data?.shipment?.subTotal != null ? data.shipment.subTotal : absoluteSubTotal);
    const effectiveVat = Number(data?.shipment?.vatAmount != null ? data.shipment.vatAmount : absoluteVatAmount);
    const effectiveInsurance = showInsurance ? absoluteInsurance : 0;
    const expectedTotal = effectiveSubTotal + effectiveVat + effectiveInsurance;
    const actualTotal = Number(data?.shipment?.total ?? data?.total);

    if (actualTotal > 0 && expectedTotal - actualTotal > 0.01) {
      const diff = Number((expectedTotal - actualTotal).toFixed(2));
      const pct = effectiveSubTotal > 0 ? (diff / effectiveSubTotal) * 100 : 0;
      const isRound = Math.abs(pct - Math.round(pct)) < 0.05;
      parsedDiscount = {
        type: isRound ? "percentage" : "fixed",
        value: isRound ? Math.round(pct) : diff,
        amount: diff,
      };
    }
  }

  const discount = parsedDiscount;
  const hasDiscount = discount && Number(discount.amount) > 0;
  const discountAmount = hasDiscount ? Number(discount.amount) : 0;

  const absoluteTotal = Math.max(0, absoluteSubTotal - discountAmount) + absoluteVatAmount + (
    showInsurance ? absoluteInsurance : 0
  );

  // "To settle" always positive; Base / Total without VAT / Invoice total always negative when non-zero
  const settleAmount = absoluteTotal;
  const signedSubTotal = isCreditInvoice ? asCreditNegative(absoluteSubTotal) : Number(data?.shipment?.subTotal || 0);
  const signedVatAmount = isCreditInvoice ? asCreditNegative(absoluteVatAmount) : Number(data?.shipment?.vatAmount || 0);
  const signedInsurance = isCreditInvoice ? asCreditNegative(absoluteInsurance) : Number(data?.shipment?.insurance || data?.insuranceAmount || 0);
  const signedTotal = isCreditInvoice ? asCreditNegative(absoluteTotal) : Number(data?.shipment?.total != null ? data.shipment.total : (data?.total != null ? data.total : (absoluteSubTotal - discountAmount + signedVatAmount + (showInsurance ? signedInsurance : 0))));

  return (
  <Document>
    <Page size="A4" style={[styles.page, hasBillTo && styles.compactPage]}>
      <View fixed style={styles.rightImage}>
        <Image src={img} style={styles.logo1} />
      </View>
      <View>
        <View style={[styles.header, hasBillTo && styles.compactHeader]}>
          <View style={{ width: "60%" }}>
            <Image src={logo} style={styles.logo} />
          </View>
          <View style={{ width: "40%", marginTop: "35" }}>
            <View style={styles.companyInfo}>
              <Text style={{ textTransform: "capitalize", fontWeight: "bold" }}>
                One Union Solutions B.V.
              </Text>
              <View style={{ rowGap: 2 }}>
                <Text>Legmeerdijk 169C,</Text>
                <Text>1432 KA Aalsmeer</Text>
                <Text>Netherlands</Text>
              </View>
              <View style={{ flexDirection: "row", marginBottom: 4, marginTop: 5 }}>
                <Text style={{ fontWeight: "bold", marginRight: 2 }}>p </Text>
                <Text>+31 850607353</Text>
              </View>
              <View style={{ flexDirection: "row", marginBottom: 4 }}>
                <Text style={{ fontWeight: "bold", marginRight: 2 }}>e </Text>
                <Text>operations@oneunionsolutions.com</Text>
              </View>
              <View style={{ flexDirection: "row", marginBottom: 4 }}>
                <Text style={{ fontWeight: "bold", marginRight: 2 }}>i </Text>
                <Text>www.oneunionsolutions.com</Text>
              </View>
            </View>
          </View>
        </View>
        <View style={[styles.paymentInfoContainer, hasBillTo && styles.compactPaymentInfoContainer]}>
          <View style={{ width: "60%", paddingLeft: "20" }}>
            <Text
              style={{
                fontWeight: "bold",
                marginTop: hasBillTo ? -72 : -80,
                marginBottom: 2,
                fontSize: 13,
                marginLeft: 15,
              }}
            >
              {isCreditInvoice ? "Credit invoice" : "Invoice"}
            </Text>
            <View style={[styles.clientInfo, hasBillTo && styles.compactClientInfo]}>
              <BillToBlock billToText={serviceRecipientText} />
              <View style={hasBillTo ? { marginTop: 6 } : {}}>
                {externalBillToLines.length ? (
                  <TextLinesBlock lines={externalBillToLines} />
                ) : (
                  <>
                    <Text style={{ fontWeight: "600" }}>
                      {data?.client?.name}
                    </Text>
                    <Text>
                      {data?.client?.address}
                    </Text>
                    <Text>
                      {data?.client?.postalCode} {data?.client?.city}
                    </Text>
                    <Text>
                      {data?.client?.country}
                    </Text>
                    <Text style={{ fontWeight: "600" }}>
                      {data?.client?.vatNumber}
                    </Text>
                  </>
                )}
              </View>
            </View>
          </View>
          <View style={{ width: "40%" }}>
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                fontSize: 9,
                marginBottom: 4,
                marginTop: hasBillTo ? 28 : -10,
              }}
            >
              <Text style={{ ...styles.titleBold, width: "20%" }}>IBAN</Text>
              <Text style={{ textAlign: "left", width: "80%" }}>
                {getInvoiceBankAccount(data).iban}
              </Text>
            </View>
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                fontSize: 9,
                marginBottom: 4,
              }}
            >
              <Text style={{ ...styles.titleBold, width: "20%" }}>SWIFT</Text>
              <Text style={{ textAlign: "left", width: "80%" }}>
                {getInvoiceBankAccount(data).swift}
              </Text>
            </View>
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                fontSize: 9,
                marginBottom: 4,
              }}
            >
              <Text style={{ ...styles.titleBold, width: "20%" }}>VAT No.</Text>
              <Text style={{ textAlign: "left", width: "80%" }}>
                NL863611394B01
              </Text>
            </View>
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                fontSize: 9,
                marginBottom: 4,
              }}
            >
              <Text style={{ ...styles.titleBold, width: "20%" }}>C.o.C.</Text>
              <Text style={{ textAlign: "left", width: "80%" }}>85402478</Text>
            </View>
          </View>
        </View>
        <View style={[styles.paymentInfoContainer, hasBillTo && styles.compactPaymentInfoContainer]}>
          <View style={{ width: "60%" }}>
            <View style={[styles.paymentInfo, hasBillTo && styles.compactPaymentInfo]}>
              <View style={styles.paymentInfoTitle}>
                <Text>Payment information</Text>
              </View>
              <View style={{ marginTop: 3, width: "100%", rowGap: 4, fontSize: 9 }}>
                {isCreditInvoice ? (
                  <>
                    <View style={{ display: "flex", flexDirection: "row" }}>
                      <Text style={{ width: "30%" }}>To settle</Text>
                      <Text
                        style={{
                          textAlign: "left",
                          width: "60%",
                          fontWeight: "bold",
                        }}
                      >
                        {getCurrencySymbol(data.shipment?.currency)}{" "}
                        {pdfAmountFormat(settleAmount, data.shipment?.currency)}
                      </Text>
                    </View>
                    <View style={{ display: "flex", flexDirection: "row", marginTop: 2 }}>
                      <Text style={{ width: "100%", lineHeight: 1.35 }}>
                        This will be settled with other invoices, or transferred to your
                        account
                      </Text>
                    </View>
                    {data?.invoiceNo && (
                      <View style={{ display: "flex", flexDirection: "row", fontSize: 9, marginTop: 2 }}>
                        <Text style={{ width: "30%" }}>Invoice No.</Text>
                        <Text
                          style={{
                            textAlign: "left",
                            width: "60%",
                            fontWeight: "bold",
                          }}
                        >
                          {data?.invoiceNo}
                        </Text>
                      </View>
                    )}
                  </>
                ) : (
                  <>
                    <View style={{ display: "flex", flexDirection: "row" }}>
                      <Text style={{ width: "30%" }}>Amount</Text>
                      <Text
                        style={{
                          textAlign: "left",
                          width: "60%",
                          fontWeight: "bold",
                        }}
                      >
                        {getCurrencySymbol(data.shipment?.currency)}{" "}
                        {pdfAmountFormat(data.shipment?.total, data.shipment?.currency)} (before{" "}
                        {data?.dueDate ? FormatDate(data.dueDate) : "N/A"})
                      </Text>
                    </View>
                    <View style={{ display: "flex", flexDirection: "row" }}>
                      <Text style={{ width: "30%" }}>IBAN/SWIFT</Text>
                      <Text
                        style={{
                          ...styles.titleBold,
                          textAlign: "left",
                          width: "70%",
                      }}
                    >
                        {getInvoiceBankAccount(data).iban}
                        {" / "}
                        {getInvoiceBankAccount(data).swift}
                      </Text>
                    </View>
                    <View style={{ display: "flex", flexDirection: "row" }}>
                      <Text style={{ width: "30%" }}>For account</Text>
                      <Text
                        style={{
                          ...styles.titleBold,
                          textAlign: "left",
                          width: "70%",
                      }}
                    >
                        {getInvoiceBankAccount(data).accountName}
                      </Text>
                    </View>
                    {data?.invoiceNo && (
                      <View style={{ display: "flex", flexDirection: "row", fontSize: 9 }}>
                        <Text style={{ width: "30%" }}>Invoice No.</Text>
                        <Text
                          style={{
                            textAlign: "left",
                            width: "60%",
                            fontWeight: "bold",
                          }}
                        >
                          {data?.invoiceNo}
                        </Text>
                      </View>
                    )}
                  </>
                )}
              </View>
            </View>
          </View>
          <View style={{ width: "40%" }}>
            <View>
              {data?.invoiceNo && (
                <View
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginBottom: 4,
                    fontSize: 9,
                  }}
                >
                  <Text style={{ width: "40%" }}>Invoice No.</Text>
                  <Text
                    style={{
                      textAlign: "left",
                      width: "60%",
                      fontWeight: "bold",
                    }}
                  >
                    {data?.invoiceNo}
                  </Text>
                </View>
              )}
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginBottom: 4,
                  fontSize: 9,
                }}
              >
                <Text style={{ width: "40%" }}>Invoice Date</Text>
                <Text
                  style={{
                    textAlign: "left",
                    width: "60%",
                    fontWeight: "bold",
                  }}
                >
                  {FormatDate(data?.invoiceDate)}
                </Text>
              </View>
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginBottom: 4,
                  fontSize: 9,
                }}
              >
                <Text style={{ width: "40%" }}>Order Reference</Text>
                <Text
                  style={{
                    textAlign: "left",
                    width: "60%",
                    fontWeight: "bold",
                    fontSize: 8.2,
                    lineHeight: 1.25,
                  }}
                >
                  {data?.orderReference ? wrapPdfText(data.orderReference) : "- -"}
                </Text>
              </View>
              {!isCreditInvoice && (
                <View
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginBottom: 4,
                    fontSize: 9,
                  }}
                >
                  <Text style={{ width: "40%" }}>Payment Within</Text>
                  <Text
                    style={{
                      textAlign: "left",
                      width: "60%",
                      fontWeight: "bold",
                    }}
                  >
                    {data?.paymentWithin} day(s)
                  </Text>
                </View>
              )}
              {isCreditInvoice && (data?.client?.customerNo || data?.customerNo || data?.userId) && (
                <View
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginBottom: 4,
                    fontSize: 9,
                  }}
                >
                  <Text style={{ width: "40%" }}>Customer No.</Text>
                  <Text
                    style={{
                      textAlign: "left",
                      width: "60%",
                      fontWeight: "bold",
                    }}
                  >
                    {data?.client?.customerNo || data?.customerNo || data?.userId}
                  </Text>
                </View>
              )}
              {!isExternalInvoice(data) && !isCreditInvoice && (
                <View
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginBottom: 4,
                    fontSize: 9,
                  }}
                >
                  <Text style={{ width: "40%" }}>Our Reference</Text>
                  <Text
                    style={{
                      textAlign: "left",
                      width: "60%",
                      fontWeight: "bold",
                    }}
                  >
                    {data?.shipment?.orderNumber || data?.orderNumber}
                  </Text>
                </View>
              )}
              {data?.collectionLocation?.trim() && (
                <View
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginBottom: 4,
                    fontSize: 9,
                  }}
                >
                  <Text style={{ width: "40%" }}>Collection</Text>
                  <Text
                    style={{
                      textAlign: "left",
                      width: "60%",
                      fontWeight: "bold",
                    }}
                  >
                    {data.collectionLocation}
                  </Text>
                </View>
              )}
              {data?.deliveryLocation?.trim() && (
                <View
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginBottom: 4,
                    fontSize: 9,
                  }}
                >
                  <Text style={{ width: "40%" }}>Delivery</Text>
                  <Text
                    style={{
                      textAlign: "left",
                      width: "60%",
                      fontWeight: "bold",
                    }}
                  >
                    {data.deliveryLocation}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
        <View style={[styles.table, hasBillTo && styles.compactTable]}>
          <View style={styles.tableHeader}>
            <Text
              style={[styles.tableCell, compactTableCellStyle, styles.tableCellHeader, { width: "68%" }]}
            >
              Description
            </Text>
            <Text
              style={[styles.tableCell, compactTableCellStyle, styles.tableCellHeader, { width: "12%" }]}
            >
              Qty
            </Text>
            <Text
              style={[styles.tableCell, compactTableCellStyle, styles.tableCellHeader, { width: "10%" }]}
            >
              Price
            </Text>
            <Text
              style={[styles.tableCell, compactTableCellStyle, styles.tableCellHeader, { width: "10%" }]}
            >
              Amount
            </Text>
          </View>
          {services.map((item, index) => {
            const qty = Number(item?.numberOfItem || 0);
            const price = Math.abs(Number(item?.exclVAT || 0));
            const absQty = Math.abs(qty);
            const absLineAmount = absQty * price;

            return (
              <View key={index} style={styles.tableRow}>
                <Text style={[styles.tableCell, compactTableCellStyle, { width: "68%" }]}>
                  {item?.description ? item?.description : "-"}
                </Text>
                <Text style={[styles.tableCell, compactTableCellStyle, { width: "12%" }]}>
                  {absQty
                    ? (isCreditInvoice ? formatCreditAmount(absQty) : pdfAmountFormat(qty))
                    : "00"}
                </Text>
                <Text style={[styles.tableCell, compactTableCellStyle, { width: "10%" }]}>
                  {price ? pdfAmountFormat(price, data.shipment?.currency) : "-"}
                </Text>
                <Text style={[styles.tableCell, compactTableCellStyle, { width: "10%" }]}>
                  {isCreditInvoice
                    ? formatCreditAmount(absLineAmount, data.shipment?.currency)
                    : pdfAmountFormat(qty * Number(item?.exclVAT || 0), data.shipment?.currency)}
                </Text>
              </View>
            );
          })}
        </View>
      </View>

      <View
        style={[
          styles.summary,
          hasBillTo && styles.compactSummary,
          hasBottomNote && { bottom: hasBillTo ? 72 : 84 },
          isCreditInvoice && { paddingTop: 6, paddingBottom: 6 },
        ]}
      >
        <View style={{ width: "58%" }}>
          {isCreditInvoice && (
            <View style={{ width: "90%" }}>
              <View style={[styles.tableHeader, { marginBottom: 2 }]}>
                <Text style={[styles.tableCell, styles.tableCellHeader, { width: "33%" }]}>Vat %</Text>
                <Text style={[styles.tableCell, styles.tableCellHeader, { width: "33%" }]}>Base</Text>
                <Text style={[styles.tableCell, styles.tableCellHeader, { width: "34%" }]}>Amount</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={[styles.tableCell, { width: "33%" }]}>
                  {pdfAmountFormat(vatPercentage)}
                </Text>
                <Text style={[styles.tableCell, { width: "33%" }]}>
                  {formatCreditAmount(absoluteSubTotal, data.shipment?.currency)}
                </Text>
                <Text style={[styles.tableCell, { width: "34%" }]}>
                  {absoluteVatAmount
                    ? formatCreditAmount(absoluteVatAmount, data.shipment?.currency)
                    : "0,00"}
                </Text>
              </View>
            </View>
          )}
        </View>
        <View style={styles.summaryContainer}>
          <View style={styles.summaryContent}>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Total without VAT</Text>
              <Text style={styles.summaryValue}>{getCurrencySymbol(data.shipment?.currency)}</Text>
              <Text style={styles.summaryTotal}>
                {isCreditInvoice
                  ? formatCreditAmount(absoluteSubTotal)
                  : pdfAmountFormat(signedSubTotal)}
              </Text>
            </View>
            {hasDiscount && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>
                  Discount ({discount.type === 'percentage' ? `${Math.abs(discount.value)}%` : 'Fixed'})
                </Text>
                <Text style={styles.summaryValue}>{getCurrencySymbol(data.shipment?.currency)}</Text>
                <Text style={styles.summaryTotal}>
                  - {pdfAmountFormat(discountAmount)}
                </Text>
              </View>
            )}
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>
                Total VAT{vatPercentage > 0 ? ` (${vatPercentage}%)` : ""}
              </Text>
              <Text style={styles.summaryValue}>{getCurrencySymbol(data.shipment?.currency)}</Text>
              <Text style={styles.summaryTotal}>
                {isCreditInvoice
                  ? (absoluteVatAmount ? formatCreditAmount(absoluteVatAmount) : "0,00")
                  : pdfAmountFormat(signedVatAmount)}
              </Text>
            </View>
            {showInsurance && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Insurance</Text>
                <Text style={styles.summaryValue}>{getCurrencySymbol(data.shipment?.currency)}</Text>
                <Text style={styles.summaryTotal}>
                  {isCreditInvoice
                    ? formatCreditAmount(absoluteInsurance)
                    : pdfAmountFormat(signedInsurance)}
                </Text>
              </View>
            )}
          </View>
          <View style={styles.divider} />
          <View style={styles.summaryRow}>
            <Text style={[styles.summaryLabel, { fontWeight: "bold" }]}>Invoice total</Text>
            <Text style={[styles.summaryValue, { fontWeight: "bold" }]}>{getCurrencySymbol(data.shipment?.currency)}</Text>
            <Text style={[styles.summaryTotal, { fontWeight: "bold" }]}>
              {isCreditInvoice
                ? formatCreditAmount(absoluteTotal)
                : pdfAmountFormat(signedTotal)}
            </Text>
          </View>
        </View>
      </View>

      {hasBottomNote && (
        <View style={styles.bottomNote} wrap={false}>
          <Text>{data.bottomNote.trim()}</Text>
        </View>
      )}

      <View style={styles.footerOne} wrap={false}>
        <View style={styles.footerTextContainer}>
          <Text style={{
            ...styles.footerTextInside,
            fontWeight: 'light', // Using lighter weight to simulate italic appearance
            textDecoration: 'underline', // Adding subtle emphasis since italic isn't working
          }}>
            This invoice and any resulting services are governed by our Terms and Conditions,
          </Text>
          <Text style={{
            ...styles.footerTextUrl,
            fontWeight: 'light', // Using lighter weight to simulate italic appearance
            textDecoration: 'underline', // Adding subtle emphasis since italic isn't working
          }}>
            which are available at https://oneunionsolutions.com/terms-and-conditions/
          </Text>
        </View>
      </View>
    </Page>
    {/*
    <Page size="A4" style={styles.page2}>
      <View style={styles.termPage} wrap={false}>
        <Image src={term} style={styles.terms} />
      </View>
    </Page>
    */}
  </Document>
  );
};

export const InvoicePDF = async (data) => {
  const pdfBlob = await pdf(<PDFDocument data={data} />).toBlob();
  return pdfBlob;
};

export default InvoicePDF;
